import {
  createContext,
  useContext,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import type { ApiProject } from "@/services/projectService";

// App-level workspace state, shared between the sidebar (the writer) and the
// canvas guide (a reader). Kept in context — not Zustand — because it is
// app-level navigation state, not canvas content. Sidebar is the source of
// truth; it fetches into `projects` and drives `activeWorkspaceId`.
interface WorkspaceCtxValue {
  projects: ApiProject[];
  setProjects: Dispatch<SetStateAction<ApiProject[]>>;
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  activeWorkspaceId: string | null;
  setActiveWorkspaceId: Dispatch<SetStateAction<string | null>>;
  // Tree/menu panel open-state. Lives here (not local to Sidebar) so the canvas
  // guide can defer to it: on mobile the tree is a canvas overlay that must
  // cover the onboarding guide when open. Sidebar remains the sole writer.
  isTreeOpen: boolean;
  setIsTreeOpen: Dispatch<SetStateAction<boolean>>;
}

export const WorkspaceCtx = createContext<WorkspaceCtxValue | null>(null);

export function WorkspaceCtxProvider({ children }: { children: ReactNode }) {
  const [projects, setProjects] = useState<ApiProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null);
  // Default open on desktop, closed on mobile — mirrors useIsMobile's
  // `(pointer: coarse)` check. Read once at mount; the media result doesn't
  // meaningfully flip mid-session, and Sidebar drives it from there.
  const [isTreeOpen, setIsTreeOpen] = useState(
    () => !window.matchMedia?.("(pointer: coarse)")?.matches,
  );
  return (
    <WorkspaceCtx.Provider
      value={{
        projects, setProjects, loading, setLoading,
        activeWorkspaceId, setActiveWorkspaceId, isTreeOpen, setIsTreeOpen,
      }}
    >
      {children}
    </WorkspaceCtx.Provider>
  );
}

export function useWorkspaceCtx(): WorkspaceCtxValue {
  const ctx = useContext(WorkspaceCtx);
  if (!ctx) throw new Error("useWorkspaceCtx: missing WorkspaceCtxProvider");
  return ctx;
}
